import { useContext, useMemo, useRef } from 'react';

import { Box, Grid, GridItem } from '@chakra-ui/react';
import { Add, Button, Card, CardContent, CardHeader, CardTitle } from '@mesh.ai/rabbit';
import OverviewSection from 'Deer/components/Overview/OverviewSection';
import { useGetMiniProfileFromEmail } from 'Deer/hooks/dataHooks/useGetMiniProfileFromEmail';
import useLocalStorageCards from 'Deer/hooks/useLocalStorageCards';
import { useRouteToNavLevels } from 'Deer/hooks/useRouteToL3';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { ProfileContext } from 'Deer/pages/Profile/hooks/ProfileContext';
import { setIsCardOpen } from 'Deer/slice/DeerCollapsibleCard.slice';
import { format } from 'date-fns';
import { capitalize } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RRule } from 'rrule';
import { useProfilePage } from 'screens/Profile/hooks/dataHooks';
import { useOneOnOneInsightsGraphData } from 'screens/oneOnOneRabbit/Insights/features/Graphs/OneOnOneCompletion/hooks/dataHooks';
import ThemeDistributionChart from 'screens/oneOnOneRabbit/Insights/features/Graphs/ThemeDistribution/ThemeDistributionChart';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';
import { generateProfilePageDataAsMiniProfile } from 'screens/oneOnOneRabbit/creationDrawer/CreateOneOnOneDrawer';
import { openCreateOneOnOneDrawer } from 'screens/oneOnOneRabbit/creationDrawer/slice/CreateOneOnOneDrawerSlice';
import { useOneOnOneLandingPageDetails } from 'screens/oneOnOneRabbit/landingPage/hooks/dataHooks';
import { selectLoggedInEmployeeId, selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled, checkNewStaticDataFeaturesEnabled } from 'utils/helpers/featureFlags';
import { getUniqueCardKey } from 'utils/helpers/getUniqueCardKey';

import noMetricData from 'assets/assetsV1/illustrations/deerIllustrations/noMetricData.svg';

import { past6Months, past12Months } from 'constants/ProjectConstants';

import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import UpComingOneOnOneList from './UpcomingOneOnOneList';

const OneOnOneOverview = () => {
  const ref = useRef();
  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const { groupByKeys } = useOneOnOneInsightsFiltersData();
  const dispatch = useDispatch();
  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData) ?? {};
  const history = useHistory();
  const employeeContext = useContext(ProfileContext);

  const employeeData = employeeContext?.id ? employeeContext : loggedInEmployeeData;

  const currentURL = history.location.pathname;

  const managerEmail = employeeData?.manager?.email;

  const { data: managerMiniProfile } = useGetMiniProfileFromEmail({ email: managerEmail });
  const { id: managerEmpId, displayName: managerName } = managerMiniProfile || {};

  // Generate the key using getUniqueCardKey function
  const localStorageKey = getUniqueCardKey({ url: currentURL, cardId: 'myDataOneOnOneOverview' });

  const isCardOpen = useLocalStorageCards({
    localStorageKey,
    card: 'oneOnOneOverview',
    section: 'myData',
  });

  const modules = useRouteToNavLevels();

  const { data: oooData, isLoading } = useOneOnOneLandingPageDetails({
    isArchivedList: false,
    selectedCohortId: null,
    dateRange: checkNewStaticDataFeaturesEnabled() ? past12Months : past6Months,
    employeedId: employeeContext?.id,
    isShowReportees: employeeContext?.id ? false : !checkNewNavEnabled() || modules?.l1Module === 'team',
  });

  // Filtering data for manager-related entries
  const filteredData = oooData?.data.filter((entry) => entry.employee?.value?.relationType === 'MANAGER');

  // @ts-expect-error in api
  const { data: bubbleChartData } = useOneOnOneInsightsGraphData({
    cohort: {
      id: -9,
    },
    isExpandedViewRequired: false,
    orderBy: null,
    dateRange: checkNewStaticDataFeaturesEnabled() ? past12Months : past6Months,
    groupByKeys,
    isManagePage: false,
    isShowReportees: employeeContext?.id ? false : !checkNewNavEnabled() || modules?.l1Module === 'team',
    employeeId: employeeContext?.id,
  });

  // Extracting essential data for manager-related entries
  const extractedDataForManager = filteredData?.map((entry) => {
    const { latestInstanceId } = entry?.nextOneOnOne?.value || {};
    const { oneOnOneMasterId: employeeOneOnOneMasterId, isOneOnOneSetup } = entry.employee.value;
    const { value: completionPercentageValue } = entry.completionPercentage || {};
    const { value: averageGapValue } = entry.averageGap || {};
    const { recurrenceDetails, nextInstanceTS } = entry?.nextOneOnOne?.value || {};
    const oneOnoneSetupDate = nextInstanceTS ? format(new Date(nextInstanceTS * 1000), 'PPp') : 'NA';
    const recurrenceData = RRule.fromString(recurrenceDetails || '');
    const oneOnOneSetupDetails = recurrenceData.toText();
    const dayCapitalizedRRuleString = oneOnOneSetupDetails
      ? `${oneOnOneSetupDetails
          .split(' ')
          .slice(0, -1)
          .map((part) => capitalize(part))
          .join(' ')} ${capitalize(oneOnOneSetupDetails.split(' ').pop())}`
      : '';
    return {
      oneOnOneMasterId: employeeOneOnOneMasterId,
      latestInstanceId,
      completionPercentageValue,
      averageGapValue,
      dayCapitalizedRRuleString,
      oneOnoneSetupDate,
      isOneOnOneSetup,
    };
  });

  //  data for rendering metrics cards for non-demo accounts
  // eslint-disable-next-line no-shadow
  const metricsCardData = extractedDataForManager?.map((data) => [
    {
      title: 'Avg. Completion % for last 6 months',
      value:
        data.completionPercentageValue !== null && data.completionPercentageValue !== undefined
          ? `${data.completionPercentageValue}%`
          : 'NA',
    },
    {
      title: 'Avg. Time Gap between 1:1s',
      value:
        data.averageGapValue !== null && data.completionPercentageValue !== undefined
          ? `${Math.floor(data.averageGapValue)} days`
          : 'NA',
    },
  ]);

  // hardcoding data for rendering metrics cards as per the ask from @abi for demo accounts
  const metricsCardDataForDemo = [
    { title: 'Time since last 1:1', value: '43 days' },
    {
      title: 'Average time gap between 1:1s',
      value: '59 days',
    },
    { title: 'Avg. Completion % for last 12 months', value: '78%' },
  ];

  const employeeMiniProfile = filteredData && filteredData[0]?.employee.value.employeeMiniProfile;

  const handleChange = (newVal: boolean) => {
    dispatch(
      setIsCardOpen({
        card: 'oneOnOneOverview',
        section: 'myData',
        isCardOpen: newVal,
        localStorageKey,
      })
    );
    return newVal;
  };

  if (isLoading) {
    return <SkeletonLoader />;
  }

  return (
    // hardcoding subtitle and subTitleColor as per the requirements to show data on trial
    <OverviewSection
      open={isCardOpen}
      onOpenChange={handleChange}
      title={managerName ? `1:1s with ${managerName} (Manager)` : '1:1s'}
      subTitle={
        checkNewStaticDataFeaturesEnabled()
          ? "Kudos on scheduling regular 1:1s with your manager! However, your current average cadence of 59 days exceeds the recommended frequency. Maven suggests considering shorter, more frequent 1:1s for optimal performance. Let's optimize your interactions for greater effectiveness!"
          : ''
      }
      subTitleColor="warning"
      showCaption={oooData?.data.length > 0 && managerName && checkNewStaticDataFeaturesEnabled() && !employeeContext}
    >
      {filteredData?.length && extractedDataForManager[0]?.isOneOnOneSetup ? (
        <Grid width="full" templateColumns={{ base: '100%', md: '3fr 2fr' }} gap="8px">
          <GridItem>
            <Box height="full">
              <Card style={{ height: '100%' }}>
                <CardHeader>
                  <CardTitle>
                    Topic distribution for last {checkNewStaticDataFeaturesEnabled() ? '12' : '6'} months
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Box h="214px" w="100%">
                    <ThemeDistributionChart data={bubbleChartData} chartRef={ref} isMyDataOverviewChart />
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </GridItem>

          <GridItem>
            <Box height="full">
              <MetricCards
                height="100%"
                columns="repeat(1, 1fr)"
                cardTitle="Metrics"
                cards={checkNewStaticDataFeaturesEnabled() ? metricsCardDataForDemo : metricsCardData[0]}
              />
            </Box>
          </GridItem>

          {employeeContext?.id && employeeContext?.id !== loggedInEmployeeId ? null : (
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <Box height="full">
                <UpComingOneOnOneList oooData={oooData?.data} />
              </Box>
            </GridItem>
          )}
        </Grid>
      ) : (
        <Card>
          <CardContent>
            <EmptyScreen
              illustrationSrc={noMetricData}
              heading={
                managerName
                  ? `1:1 with ${managerName} is not setup`
                  : `${
                      employeeContext?.id ? `${employeeContext?.displayName} doesn’t` : 'You don’t'
                    } have a manager on Mesh`
              }
              description={
                managerName
                  ? 'Regular 1:1’s with your manager are essential for fostering open communication, addressing concerns, and ensuring alignment'
                  : `${
                      employeeContext?.id ? 'This' : 'Your'
                    } profile does not currently list a manager. If this information is not accurate, please contact your HR administrator.`
              }
              isSmallTextSize
              illustrationStyle={{
                width: '180px',
                height: '160px',
              }}
              // eslint-disable-next-line react/no-unstable-nested-components
              CTAComp={() =>
                managerName && (
                  <Button
                    onClick={() =>
                      dispatch(
                        openCreateOneOnOneDrawer({
                          excludedParticipantIds: [],
                          employeeId: employeeMiniProfile?.id ?? managerEmpId,
                          employeeMiniProfile: employeeMiniProfile ?? managerMiniProfile,
                          startDateTime: undefined,
                          duration: 0,
                          frequency: '',
                          participantId: 0,
                          isCalendarSynced: false,
                          relation: '',
                        })
                      )
                    }
                    variant="outline"
                    iconLeft={Add}
                  >
                    Setup 1:1
                  </Button>
                )
              }
            />
          </CardContent>
        </Card>
      )}
    </OverviewSection>
  );
};

export default OneOnOneOverview;
